import React, { useContext, useCallback } from 'react';
import {
  arrayOf, bool, string, shape, func
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Button, CardMedia } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { useOnScreen } from '../../../hooks/useOnScreen';

export const CategoryNav = ({ categories, forwardRef, showSelected, onClick }) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const categoryRefs = categories.map((category) => category.sectionRef);
  const { intersectedRefIndex: visibleSection } = useOnScreen(categoryRefs);

  const handleClick = useCallback((category) => {
    const NAV_HEIGHT_OFFSET = 39;
    onClick(category);
    const offsetTop = category.sectionRef.current?.offsetTop;
    window.scroll({
      top: offsetTop - NAV_HEIGHT_OFFSET,
      behavior: 'smooth'
    });
  }, [onClick]);
  const shadowCss = classNames('sui-flex', { 'sui-shadow-lg': showSelected });

  return (
    <div ref={forwardRef} className={shadowCss}>
      {categories.map((category, sectionIndex) => {
        const { title, id, imageURL } = category;
        const isCurrentSection = sectionIndex === visibleSection;
        const isSelectedCss = classNames(
          'sui-flex sui-paper sui-w-full sui-items-center', {
            'sui-h-14 sui-p-2': !isMobile,
            'sui-p-3': isMobile,
            'sui-border-solid sui-border-accent sui-border-b-[4px] sui-font-bold': showSelected && isCurrentSection
          });
        const textCss = classNames('sui-flex-auto sui-line-clamp-2 sui-text-ellipsis sui-overflow-hidden', {
          'sui-text-2xl': !isMobile,
          'sui-text-info': !isCurrentSection,
        });
        return (
          <Button
            type="button"
            onClick={() => handleClick(category)}
            key={id}
            className={isSelectedCss}
          >
            {!isMobile && (
              <CardMedia
                alt={title}
                src={imageURL}
                className="sui-h-11 sui-w-11"
              />
            )}
            <span className={textCss}>{title}</span>
          </Button>
        );
      })}
    </div>
  );
};

CategoryNav.displayName = 'CategoryNav';

CategoryNav.propTypes = {
  categories: arrayOf(
    shape({
      title: string,
      imageURL: string,
      id: string.isRequired
    })
  ).isRequired,
  forwardRef: shape({}),
  showSelected: bool,
  onClick: func.isRequired
};

CategoryNav.defaultProps = {
  forwardRef: null,
  showSelected: false
};
